import { IncorporationRequestDataType } from '@commenda/types';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { EDUCATIONAL_QUALIFICATION_TYPE_ENUM_TO_TEXT } from '@commenda/utils';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';
import { WorkRequestType } from '@prisma/client';
import { Country, State } from 'country-state-city';
import { DocumentSummaryField, SummaryFieldKS } from '../../common';
import { getDirectorName } from '../../Director/common';
import { getIdentityProof } from '../utils';

const USFounderField = ({
  director,
  data,
  workRequestType,
}: {
  director: DirectorBase;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  workRequestType?: WorkRequestType;
}) => {
  const directorVsEquityMap: Record<
    number,
    { sharePercentage: number; name: string; id: number }
  > = {};

  data?.directors?.forEach((dir) => {
    const details = dir.corporationHasDirector.find(
      (directorWithCorporation) =>
        directorWithCorporation.corporationId === data.corporationId
    )?.additionalDirectorInformation;

    directorVsEquityMap[dir.id] = {
      name: getDirectorName(dir),
      sharePercentage: details?.sharePercentage,
      id: dir.id,
    };
  });

  const currentAddress = director?.operatingAddress;
  const DIN = director?.directorIdentificationNumber;
  const educationalQualification =
    director?.personalDetails?.educationalQualification;

  const identityProof = getIdentityProof(director, data.corporationId);

  const sharePercentage = director?.corporationHasDirector?.find(
    (dirWithCorp) =>
      dirWithCorp.directorId === director.id &&
      dirWithCorp.corporationId === data.corporationId
  )?.additionalDirectorInformation?.sharePercentage;

  const openFile = async (fileId: number) => {
    const res = await getSignedUrlByFileId(fileId, null);
    window.open(res);
  };

  return (
    <div className="p-6 flex flex-col gap-2 bg-bg-disabled-subtle">
      <SummaryFieldKS 
        heading="First Name" 
        value={director?.directorFirstName || '-'} 
      />
      <SummaryFieldKS 
        heading="Last Name" 
        value={director?.directorLastName || '-'} 
      />
      
      <SummaryFieldKS heading="Phone" value={director?.number} />

      <SummaryFieldKS heading="Email address" value={director?.email} />

      {director?.dateOfBirth && (
        <SummaryFieldKS heading="Date of Birth" value={director.dateOfBirth} />
      )}

      <SummaryFieldKS
        heading="Has Digital Signature Certificate"
        value={director?.personalDetails?.hasDSC ? 'Yes' : 'No'}
      />

      {DIN ? <SummaryFieldKS heading="DIN" value={DIN} /> : null}

      {director?.personalDetails?.nationality ? (
        <SummaryFieldKS
          heading="Nationality"
          value={
            Country.getCountryByCode(director?.personalDetails?.nationality)
              ?.name
          }
        />
      ) : null}

      {director?.personalDetails?.residence && (
        <SummaryFieldKS
          heading="Country of Residence"
          value={
            Country.getCountryByCode(director?.personalDetails?.residence)?.name
          }
        />
      )}

      {educationalQualification ? (
        <SummaryFieldKS
          heading="Education Qualification"
          value={
            EDUCATIONAL_QUALIFICATION_TYPE_ENUM_TO_TEXT[
              educationalQualification
            ]
          }
        />
      ) : null}

      {director.personalDetails?.occupation ? (
        <SummaryFieldKS
          heading="Occupation"
          value={director.personalDetails?.occupation}
        />
      ) : null}
      {director.personalDetails?.placeOfBirth ? (
        <SummaryFieldKS
          heading="Place of Birth"
          value={`${director?.personalDetails?.placeOfBirth?.district}, ${State.getStateByCodeAndCountry(director?.personalDetails?.placeOfBirth?.state, director?.personalDetails?.placeOfBirth?.country)?.name},  ${Country.getCountryByCode(director?.personalDetails?.placeOfBirth?.country)?.name}`}
        />
      ) : null}

      {currentAddress ? (
        <SummaryFieldKS
          heading="Current address"
          value={[
            `${currentAddress?.addressLine1}, ${currentAddress?.addressLine2}`,
            `${currentAddress?.city} - ${currentAddress?.postalCode}`,
            `${
              State.getStateByCodeAndCountry(
                currentAddress?.state,
                currentAddress?.country
              )?.name
            }, ${Country.getCountryByCode(currentAddress?.country)?.name}`,
          ]}
        />
      ) : null}

      {director?.addressProof?.fileUrl ? (
        <DocumentSummaryField
          onClick={() => openFile(director?.addressProof?.id)}
          title="Proof of Address"
          className="grid grid-cols-2"
        />
      ) : null}

      {director?.aadharCard?.fileUrl ? (
        <DocumentSummaryField
          onClick={() => openFile(director?.aadharCard?.id)}
          title="Aadhar Card"
          className="grid grid-cols-2"
        />
      ) : null}

      {director?.panCard?.fileUrl ? (
        <DocumentSummaryField
          onClick={() => openFile(director?.panCard?.id)}
          title="Pan Card"
          className="grid grid-cols-2"
        />
      ) : null}
      {identityProof?.fileUrl ? (
        <DocumentSummaryField
          onClick={() => openFile(identityProof?.id)}
          title="Identity Proof"
          className="grid grid-cols-2"
        />
      ) : null}
    </div>
  );
};

export default USFounderField;
